import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, Button, IconButton, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { forgotPasswordAction, resetPasswordAction,clearResetPasswordAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Authenticate, verifyOTP, initOTPless } from '../../utils/initOtpless.js';
import toast from 'react-hot-toast';

const ForgotPasswordModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(120);
  const [otpExpired, setOtpExpired] = useState(false);

  const { successData, error, errorInfo } = useSelector(state => state.forgotPasswordReducer);
  const { successData: resetPasswordData, error: resetPasswordError, errorInfo: resetPasswordErrorInfo } = useSelector(state => state.resetPasswordReducer);

  useEffect(() => {
    initOTPless();
  }, []);

  const formik = useFormik({
    initialValues: {
      phone_number: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      phone_number: Yup.string().required('phoneNumber is required'),
      otp: Yup.string().when('otpSent', {
        is: true,
        then: Yup.string().required('OTP is required'),
      }),
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("New Password is required"),
      confirmPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values) => {
      if (!otpSent) {
        try {
          const payload = {
            mobile_no: values.phone_number,
          };
          dispatch(forgotPasswordAction(payload));
          setOtpSent(true);
          setTimer(120);
          setOtpExpired(false);
          console.log('Sending OTP to:', values.phone_number);
        } catch (error) {
          console.error('Error sending OTP:', error);
        }
      } else {
        try {
          console.log('OTP verified, resetting password for:', values.phone_number);
          // Reset password logic here
          onClose();
        } catch (error) {
          console.error('Error verifying OTP:', error);
        }
      console.log('OTP sent successfully');  
      }
    },
  });

  const handleResendOtp = () => {
    formik.handleSubmit();
  };

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setOtpExpired(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  useEffect(() => {
    if (successData) {
      Authenticate({ channel: 'PHONE', phone: successData.mobile_no, countryCode: '+91' });
      toast.success(successData.message);
    }
    if(error) {
      toast.error(errorInfo.message);
    }
  }, [successData, error, errorInfo]);

  const handleVerifyOtp = async () => {
    try {
      if (formik.values.newPassword !== formik.values.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      
      const response = await verifyOTP({ 
        otp: formik.values.otp, 
        channel: 'PHONE', 
        phone: successData.mobile_no, 
        countryCode: '+91' 
      });
      
      if (response.success) {
        dispatch(resetPasswordAction({ 
          mobile_no: successData.mobile_no, 
          status: response.success, 
          newPassword: formik.values.newPassword 
        }));
      } else {
        toast.error('Invalid OTP');
      }
    } catch (error) {
      toast.error('Error verifying OTP');
    }
    console.log(formik.values);
  };
  
  useEffect(() => {
    if (resetPasswordData) {
      toast.success(resetPasswordData.message);
      onClose();
        formik.resetForm();
        setOtpSent(false);
    }
    if(resetPasswordError) {
      toast.error(resetPasswordErrorInfo);
    }
    return () => {
      dispatch(clearResetPasswordAction())
    }
  }, [resetPasswordData, resetPasswordError, resetPasswordErrorInfo])

  useEffect(() => {
    return () => {
      formik.resetForm();
      setOtpSent(false);
      setTimer(120);
      setOtpExpired(false);
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} disableScrollLock={true} sx={{ '& .MuiDialog-paper': { width: '500px', borderRadius: '15px' } }}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Forgot Password
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            autoFocus
            margin="dense"
            id="phone_number"
            label="phone_number"
            type="text"
            fullWidth
            variant="outlined"
            {...formik.getFieldProps('phone_number')}
            error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
            helperText={formik.touched.phone_number && formik.errors.phone_number}
          />
          {otpSent && (
            <>
              <TextField
                margin="dense"
                id="otp"
                label="OTP"
                type="text"
                fullWidth
                variant="outlined"
                {...formik.getFieldProps('otp')}
                error={formik.touched.otp && Boolean(formik.errors.otp)}
                helperText={formik.touched.otp && formik.errors.otp}
              />
              {otpExpired ? (
                <Alert severity="warning" sx={{ width: '100%', mb: 2, padding: '0px', fontSize: '0.875rem' }}>
                  Your OTP has expired. <Button onClick={handleResendOtp} sx={{ color: '#FC903D' }}>Resend OTP</Button>
                </Alert>
              ) : (
                <Typography sx={{ color: 'gray', mb: 2 }}>
                  OTP expires in {timer} seconds
                </Typography>
              )}
            </>
          )}
          <TextField
            margin="dense"
            id="newPassword"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            {...formik.getFieldProps('newPassword')}
            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
          <TextField
            margin="dense"
            id="confirmPassword"
            label="Confirm Password"
            type="password"
            fullWidth
            variant="outlined"
            {...formik.getFieldProps('confirmPassword')}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
          {!otpSent ? (
            <Button type="submit" sx={{ mt: 2, backgroundColor: '#1814F3', color: '#fff' }}>
              Send OTP
            </Button>
          ) : (
            <Button type="button" sx={{ mt: 2, backgroundColor: '#1814F3', color: '#fff' }} onClick={handleVerifyOtp}>
              Submit
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;